import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "./api";

// ─── useGpsDeparture ───────────────────────────────────────────────────
//
// Inverse of `useGpsProximity` in field/job-detail.tsx — fires exactly
// once when the device has been *outside* the configured site geofence
// for at least `graceSeconds` while the job is still in `arrived` /
// `in_progress` (i.e. not yet closed out).
//
// Used by the field LeavingSiteCloseoutModal to nag a tech who drives
// away from the customer's site without picking a terminal status. The
// `onDeparture` callback fires only on the rising edge so re-triggering
// is opt-in via `reset()`.

export interface UseGpsDepartureOptions {
    /** Site latitude (WGS84). Pass `null` to disable. */
    siteLat: number | null;
    /** Site longitude (WGS84). Pass `null` to disable. */
    siteLng: number | null;
    /** Whether the watcher should be active. Should reflect:
     *    - job assigned to this tech
     *    - job status in {arrived, in_progress}
     *    - arrived_at IS NOT NULL (we know they made it on-site)
     *    - the closeout modal isn't already open */
    enabled: boolean;
    /**
     * Fires exactly once when the device has been outside the radius
     * for >= `graceSeconds`. Receives the most recent distance + GPS
     * fix so the caller can post a /departure-detected audit row.
     */
    onDeparture: (info: { distanceM: number; lat: number; lng: number }) => void;
    /** Optional override (m). Falls back to /api/settings/geofence. */
    radiusOverrideM?: number;
    /** Optional override (s). Falls back to /api/settings/geofence. */
    graceOverrideS?: number;
}

export interface UseGpsDepartureResult {
    /** Most recent distance from the site (meters). null until first fix. */
    distance: number | null;
    /** Whether the device has been outside the radius long enough to fire. */
    departed: boolean;
    /** Why the watcher is currently dormant (for tech-side debugging). */
    inactiveReason: "disabled" | "no-coords" | "no-permission" | "watching" | "fired";
    /** Re-arm the rising-edge detector after the modal is dismissed. */
    reset: () => void;
}

const DEFAULT_RADIUS_M = 150;
const DEFAULT_GRACE_S = 60;

interface GeofenceSettings {
    radiusM: number;
    graceSeconds: number;
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useGpsDeparture({
    siteLat,
    siteLng,
    enabled,
    onDeparture,
    radiusOverrideM,
    graceOverrideS,
}: UseGpsDepartureOptions): UseGpsDepartureResult {
    const settings = useQuery({
        queryKey: ["settings", "geofence"],
        queryFn: () => api.get<GeofenceSettings>("/api/settings/geofence"),
        staleTime: 10 * 60_000,
        retry: 1,
    });

    const radiusM = radiusOverrideM ?? settings.data?.radiusM ?? DEFAULT_RADIUS_M;
    const graceS = graceOverrideS ?? settings.data?.graceSeconds ?? DEFAULT_GRACE_S;

    const [distance, setDistance] = useState<number | null>(null);
    const [departed, setDeparted] = useState(false);
    const [inactiveReason, setInactiveReason] = useState<UseGpsDepartureResult["inactiveReason"]>("disabled");

    // Stable refs so we don't re-arm the watcher on every render.
    const onDepartureRef = useRef(onDeparture);
    onDepartureRef.current = onDeparture;
    const outsideStartRef = useRef<number | null>(null);
    const firedRef = useRef(false);

    const reset = () => {
        firedRef.current = false;
        setDeparted(false);
        outsideStartRef.current = null;
    };

    useEffect(() => {
        if (!enabled) {
            setInactiveReason("disabled");
            outsideStartRef.current = null;
            return;
        }
        if (siteLat == null || siteLng == null) {
            setInactiveReason("no-coords");
            return;
        }
        if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
            setInactiveReason("no-permission");
            return;
        }
        setInactiveReason("watching");
        const id = navigator.geolocation.watchPosition(
            (pos) => {
                if (firedRef.current) return; // already fired
                const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, siteLat, siteLng);
                setDistance(Math.round(d));
                if (d > radiusM) {
                    if (!outsideStartRef.current) {
                        outsideStartRef.current = Date.now();
                    }
                    const outsideMs = Date.now() - outsideStartRef.current;
                    if (outsideMs >= graceS * 1000) {
                        firedRef.current = true;
                        setDeparted(true);
                        setInactiveReason("fired");
                        onDepartureRef.current({
                            distanceM: Math.round(d),
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                        });
                    }
                } else {
                    // Re-entered the radius — reset the outside timer
                    // (covers the "tech got out, walked the property,
                    // came back" case).
                    outsideStartRef.current = null;
                }
            },
            () => {
                setInactiveReason("no-permission");
            },
            { enableHighAccuracy: true, maximumAge: 15_000, timeout: 15_000 },
        );
        return () => navigator.geolocation.clearWatch(id);
    }, [enabled, siteLat, siteLng, radiusM, graceS]);

    return { distance, departed, inactiveReason, reset };
}
